<template>
  <div class="fill-height overflow-hidden">
    <ParticipantVideoRoom>
      <VideoPlayer
        v-if="playerVisible"
        :volume="volume"
        :isMuted="$store.state.videoMuted"
        @ended="showGoToLobby = true"
      >
        <ParticipantTeamCaptainStream
          ref="teamCaptainStream"
          class="teamcaptain-video"
          @publish="publishing = true"
          @unpublish="publishing = false"
          @receiving="receiving = true"
          @stop="receiving = false"
        />
        <transition-group name="reaction">
          <div v-for="entry in reactionsBuffer" :key="entry.timestamp">
            <div
              v-for="(nothing, index) in Array(entry.nbItems)"
              :key="index"
              class="animate__animated animate__fadeOutUpBig animate__slower"
              :style="{
                position: 'absolute',
                'animation-delay': Math.round(200 * Math.random()) + 'ms',
                left: ((900 + 200 * Math.random()) * index) / entry.nbItems / 10 + '%',
              }"
            >
              <v-btn small fab rounded class="animate__animated animate__wobble">
                <img class="emoji-icon" alt="emoji" :src="entry.icon" />
              </v-btn>
            </div>
          </div>
        </transition-group>
      </VideoPlayer>
    </ParticipantVideoRoom>
    <v-toolbar flat color="transparent">
      <template left v-if="$store.state.event.enableLobby">
        <v-btn color="primary" :href="getLobbyUrl()">
          {{ $t("live.activity.lobby.goBackToLobby") }}
        </v-btn>
      </template>
      <!--<v-spacer></v-spacer>-->
      <v-spacer></v-spacer>
      <v-btn
        v-for="(emote, index) in $store.state.selectedActivity.enableEmotes ? emotes : []"
        class="ml-1 mr-1 reaction-btn"
        v-bind:key="index"
        @click.prevent="sendEmote(emote)"
        :retain-focus-on-click="false"
        small
      >
        <img class="emoji-icon" alt="emoji" :src="emote.icon" />
      </v-btn>
      <v-spacer></v-spacer>
      <ParticipantTeamCaptainActions
        v-if="$store.state.selectedActivity.enableTeamCaptainSpeech && $route.query.teamCaptain"
        @click="$refs.teamCaptainStream.publishTeamCaptainStream()"
        :publishing="publishing"
        :receiving="receiving"
      />
      <VideoFeedback v-if="$store.state.selectedActivity.enableVideoFeedback" />
      <ParticipantVideoRooms v-if="$store.state.selectedActivity.enableVideoChat" />
    </v-toolbar>
    <v-container fluid class="px-1 pt-0 pb-4">
      <v-row justify="center" dense class="fill-height">
        <v-col cols="12" md="6" xl="4">
          <v-card
            v-if="parsedSchedule && parsedSchedule.length > 0"
            @click.native="displayFullSchedule = !displayFullSchedule"
          >
            <v-card-title v-if="$vuetify.breakpoint.mdAndUp" class="subtitle-2">
              <v-icon>mdi-clock-outline</v-icon>
              <span class="ml-2">{{ $t("live.activity.schedule.title") }}</span>
            </v-card-title>
            <v-card-text class="pa-0 pa-md-2 pa-lg-4 pt-md-0 pt-lg-0">
              <v-slide-y-transition class="py-0 body-2" group tag="v-list">
                <template v-for="(schedule, index) in displayFullSchedule ? parsedSchedule : shortSchedule">
                  <v-divider v-if="index !== 0" :key="`${index}-divider`" />
                  <v-list-item
                    :key="index"
                    dense
                    :class="{
                      'pl-0': true,
                      'ended-schedule': isPastScheduleEntry(schedule),
                    }"
                  >
                    <span class="mr-4 text-right" style="min-width: 40px">{{
                      schedule.startMoment.format("HH:mm")
                    }}</span>
                    <v-list-item-content class="py-0">
                      {{ schedule.title }}
                    </v-list-item-content>
                    <v-list-item-action class="my-0">
                      <div class="d-flex" v-if="schedule === currentScheduleEntry">
                        <v-btn
                          @click.stop.prevent="speedUpSchedule(schedule)"
                          icon
                          color="primary"
                          large
                          class="ml-3"
                          v-if="$store.state.me && $store.state.me.appAdmin"
                          title="Reduce countdown (Test mode)"
                        >
                          <v-icon>mdi-clock-fast</v-icon>
                        </v-btn>
                        <v-icon class="mx-3">mdi-autorenew mdi-spin </v-icon>
                      </div>
                    </v-list-item-action>
                  </v-list-item>
                </template>
              </v-slide-y-transition>
            </v-card-text>
          </v-card>
        </v-col>
        <v-col
          v-if="geolocationMap"
          cols="12"
          md="6"
          xl="4"
          v-show="!runEnded || distance > 0 || $store.state.selfies.length || $store.state.challengePerformance"
        >
          <v-card>
            <v-row no-gutters>
              <v-col cols="7">
                <v-window :show-arrows="runEnded">
                  <v-window-item class="overlay-container" v-if="!runEnded || distance > 0">
                    <GmapMap
                      :zoom="14"
                      :center="mapCenter"
                      ref="mapRef"
                      class="gmap"
                      map-type-id="terrain"
                      :options="{
                        fullscreenControl: false,
                      }"
                    >
                      <gmap-polyline
                        v-if="$store.state.geolocation.trackHistory.length > 1"
                        :path="$store.state.geolocation.trackHistory"
                        :options="{ geodesic: true, strokeColor: '#FF0000' }"
                      />
                      <GmapMarker
                        :clickable="false"
                        :draggable="false"
                        :position="$store.state.geolocation.position"
                        @click="center = $store.state.geolocation.position"
                        v-if="$store.state.geolocation.position"
                      />
                    </GmapMap>
                    <v-overlay absolute v-if="!$store.state.geolocation.position" opacity="0.8">
                      <v-container class="text-center" v-if="!runEnded">
                        <div class="mb-2">{{ $t("live.activity.geolocation.overlay.title") }}</div>
                        <v-btn
                          color="primary"
                          @click="enableGeolocation()"
                          :loading="$store.state.geolocation.positionWatch !== null"
                          >{{ $t("live.activity.geolocation.overlay.allow") }}</v-btn
                        >
                      </v-container>
                    </v-overlay>
                  </v-window-item>
                  <v-window-item v-for="selfie in $store.state.selfies" :key="selfie.id">
                    <v-img :src="selfie && selfie.imageServeUrl" max-height="250px">
                      <v-overlay absolute opacity="0.9" class="image-share-overlay">
                        <ShareButton :selfie="selfie" />
                      </v-overlay>
                    </v-img>
                  </v-window-item>
                </v-window>
              </v-col>
              <v-col cols="5">
                <v-list>
                  <v-list-item v-if="distance > 0" class="px-1 text-center">
                    <v-list-item-content>
                      <v-list-item-subtitle>{{ $t("live.activity.distance.distance") }}</v-list-item-subtitle>
                      <v-list-item-title class="title">
                        <v-icon class="mr-1">mdi-run</v-icon>
                        <span>{{ distance }} m</span>
                      </v-list-item-title>
                    </v-list-item-content>
                  </v-list-item>
                  <v-list-item v-else class="px-1 text-center">
                    <v-list-item-content>
                      <v-list-item-subtitle>{{ $t("live.activity.distance.challenge") }}</v-list-item-subtitle>
                      <v-list-item-title class="title">
                        <v-icon class="mr-1">mdi-run</v-icon>
                        <span v-if="currentScheduleEntry && currentScheduleEntry.enableDistanceTracking && !runEnded">{{
                          $t("live.activity.distance.start")
                        }}</span>
                        <span
                          v-else-if="currentScheduleEntry && currentScheduleEntry.enableRequestGeolocation && !runEnded"
                          >{{ $t("live.activity.distance.getready") }}</span
                        >
                        <span class="caption font-weight-bold" v-else-if="$store.state.challengePerformance">{{
                          $store.state.challengePerformance
                        }}</span>
                        <span v-else-if="runEnded">{{ $t("live.activity.distance.ended") }}</span>
                      </v-list-item-title>
                    </v-list-item-content>
                  </v-list-item>
                  <v-list-item v-if="!runEnded && this.distanceTrackingStarted" class="px-1 text-center">
                    <v-list-item-content>
                      <v-list-item-subtitle>{{ $t("live.activity.distance.completed") }}</v-list-item-subtitle>
                      <v-list-item-title class="title">
                        <v-btn color="primary" outlined @click="completeChallenge()">{{
                          $t("live.activity.distance.complete")
                        }}</v-btn>
                      </v-list-item-title>
                    </v-list-item-content>
                  </v-list-item>
                  <v-list-item
                    v-else-if="currentScheduleEntry && currentScheduleEntry.enableRequestGeolocation"
                    class="px-1 text-center"
                  >
                    <v-list-item-content>
                      <v-list-item-subtitle>{{ $t("live.activity.distance.startsIn") }}</v-list-item-subtitle>
                      <v-list-item-title class="title">
                        <v-icon class="mr-1">mdi-clock-outline</v-icon>
                        <span>{{ this.timeToScheduleEntry(this.nextScheduleEntry) }}</span>
                      </v-list-item-title>
                    </v-list-item-content>
                  </v-list-item>
                </v-list>
              </v-col>
            </v-row>
            <div class="red text-center" v-if="$store.state.geolocation.deniedByUser && !runEnded">
              {{ $t("live.activity.distance.deniedByUser") }}
            </div>
          </v-card>
        </v-col>
        <v-col v-if="selfieWallEmbedCode" cols="12" md="6" xl="4">
          <ParticipantWall
            ref="participantWall"
            :activity-id="$store.state.selectedActivity.id"
            :embedSrc="selfieWallEmbedCode"
          />
        </v-col>
      </v-row>
    </v-container>
    <ParticipantPrepareChallengeModal
      :runActivityScheduleEnded="runActivityScheduleEnded"
      :runEnded="runEnded"
      :schedule="currentScheduleEntry"
      @runEnded="completeChallenge()"
      @selfieUploaded="selfieUploaded()"
      @cancelPerformance="runEnded = false"
    />
    <v-dialog v-if="triggerPositionUnavailableDialog" v-model="challengePositionUnavailableDialog" width="500">
      <ParticipantChallengePositionUnavailable
        @continue="challengePositionUnavailableDialog = false"
      ></ParticipantChallengePositionUnavailable>
    </v-dialog>
  </div>
</template>

<script>
import moment, { now } from "moment";
import VideoFeedback from "./components/participant.video_feedback";
import VideoPlayer from "./components/video.player";
import ParticipantVideoRooms from "./components/participant.video_rooms";
import ParticipantVideoRoom from "./components/participant.video_room";
import ParticipantTeamCaptainStream from "./components/participant.team_captain_stream";
import ParticipantTeamCaptainActions from "./components/participant.team_captain_actions";
import ParticipantPrepareChallengeModal from "./components/participant.prepare.challenge.modal";
import ParticipantChallengePositionUnavailable from "./components/participant.challenge_position_unavailable";
import axios from "axios";
import ParticipantWall from "./components/participant.wall";
import ShareButton from "./components/share_button";

const VOLUME_MIN = 30;
const VOLUME_MAX = 100;

export default {
  data() {
    return {
      playerVisible: true,
      triggerPositionUnavailableDialog: false,
      challengePositionUnavailableDialog: true,
      runEnded: this.$store.state.participantChallengeDeclared,
      runActivityScheduleEnded: false,
      geolocationMap: false,
      distanceTrackingStarted: false,
      videoLinkNormalised: null,
      videoMode: null,
      publishing: false,
      receiving: false,
      reactionNotificationInterval: null,
      participantWallInterval: null,
      displayFullSchedule: false,
      selfieWallEmbedCode: false,
      emotes: [
        {
          icon: "/assets/aprove.png",
        },
        {
          icon: "/assets/heart.png",
        },
        {
          icon: "/assets/laugh.png",
        },
        {
          icon: "/assets/applause.png",
        },
      ],
      reactionsBuffer: [],
      participant: {},
    };
  },
  components: {
    ParticipantWall,
    ParticipantPrepareChallengeModal,
    ParticipantTeamCaptainActions,
    ParticipantTeamCaptainStream,
    ParticipantVideoRooms,
    ParticipantVideoRoom,
    VideoPlayer,
    VideoFeedback,
    ShareButton,
    ParticipantChallengePositionUnavailable,
  },
  computed: {
    volume() {
      return this.publishing || this.receiving ? VOLUME_MIN : VOLUME_MAX;
    },
    parsedSchedule() {
      if (!this.$store.state.selectedActivity.schedule) return null;
      const startDur = moment.duration();
      return this.$store.state.selectedActivity.schedule
        .filter((i) => i.public)
        .map((item) => {
          const st = moment.duration(startDur);
          startDur.add(item.durationSeconds, "s");
          return {
            ...item,
            startDur: st,
            endDur: moment.duration(startDur),
            startMoment: moment(
              this.$store.state.selectedActivity.date || this.$store.state.selectedActivityStartDate
            ).add(st),
          };
        });
    },
    shortSchedule() {
      return (
        this.parsedSchedule &&
        this.parsedSchedule
          .filter((s, i) => !this.isPastScheduleEntry(s) || (i === this.parsedSchedule.length - 1 && s.public))
          .slice(0, 2)
      );
    },
    currentScheduleEntry() {
      return this.shortSchedule && this.shortSchedule.length ? this.shortSchedule[0] : {};
    },
    elapsedTime() {
      if (!this.$store.state.selectedActivity.date || !this.$store.state.serverTime) {
        return null;
      }
      return moment.duration(this.$store.state.serverTime.diff(moment(this.$store.state.selectedActivity.date)));
    },
    nextScheduleEntry() {
      return this.parsedSchedule[this.parsedSchedule.indexOf(this.currentScheduleEntry) + 1];
    },
    mapCenter() {
      return this.$store.state.geolocation.position || { lat: 0, lng: 0 };
    },
    distance() {
      return Math.round(this.$store.state.geolocation.distance);
    },
    secondsToNextScheduleEntry() {
      return this.nextScheduleEntry && this.timeToSchedule(this.nextScheduleEntry).asSeconds();
    },
  },
  watch: {
    "$store.state.selectedActivity.link": {
      handler() {
        this.playerVisible = false;
        this.$store.commit("VIDEO_MUTED", true);
        this.$nextTick(() => {
          this.playerVisible = true;
        });
      },
    },
    secondsToNextScheduleEntry(newVal) {
      if (newVal && newVal <= 6 && this.nextScheduleEntry.enableDistanceTracking) {
        this.scheduleLocationCheck();
      }
    },
    currentScheduleEntry(newScheduleEntry, oldScheduleEntry) {
      const newTrackingEnabled = newScheduleEntry && newScheduleEntry.enableDistanceTracking;
      const oldTrackingEnabled = oldScheduleEntry && oldScheduleEntry.enableDistanceTracking;
      if (newTrackingEnabled) {
        this.$store.commit("TRACKING_ENABLED", true);
      }
      if (oldTrackingEnabled && !newTrackingEnabled) {
        this.runActivityScheduleEnded = true;
      }
      if (newScheduleEntry) {
        if (newScheduleEntry.enableRequestGeolocation || newScheduleEntry.enableDistanceTracking) {
          this.geolocationMap = true;
        }

        if (newScheduleEntry.enableDistanceTracking) {
          this.distanceTrackingStarted = true;
        }

        if (newScheduleEntry.selfieWallEmbedCode) {
          this.selfieWallEmbedCode = newScheduleEntry.selfieWallEmbedCode;
        }
      }
    },
    reactionsBuffer(val) {
      if (val && val.length > 0) {
        setTimeout(() => val.unshift(), 5000);
      }
    },
    distance(distance) {
      if (distance > 0) {
        axios.post(
          `/event/activities/session/${this.$store.state.sessionId}/participant/${this.$store.state.participantId}/declareDistance`,
          {
            declaredDistance: distance,
          }
        );
      }
    },
  },
  methods: {
    async enableGeolocation() {
      try {
        await this.$store.dispatch("ENABLE_GEOLOCATION");
      } catch (err) {
        console.log(err);
      }
    },
    isCurrentScheduleEntry(entry) {
      return (
        this.elapsedTime.asMilliseconds() >= entry.startDur.asMilliseconds() &&
        this.elapsedTime.asMilliseconds() < entry.endDur.asMilliseconds()
      );
    },
    isPastScheduleEntry(entry) {
      if (this.elapsedTime) {
        return this.elapsedTime.asMilliseconds() > entry.endDur.asMilliseconds();
      }
    },
    timeToSchedule(entry) {
      return moment.duration(entry.startDur - this.elapsedTime);
    },
    iOS() {
      return (
        /iPad|iPhone|iPod/.test(navigator.platform) ||
        (navigator.platform === "MacIntel" && navigator.maxTouchPoints > 1)
      );
    },
    displayReaction(reaction) {
      this.reactionsBuffer.push({
        timestamp: now(),
        icon: reaction.icon,
        nbItems: this.$vuetify.breakpoint.smAndUp
          ? Math.round(this.$vuetify.breakpoint.width / 100)
          : Math.round(this.$vuetify.breakpoint.width / 50),
      });
    },
    initEmotes() {
      const session = this.$store.state.sessionObject;
      session.on("signal:userReaction", (event) => {
        const reaction = JSON.parse(event.data);
        const who = this.$store.state.participants.find((i) => i.id === reaction.who);
        this.$store.commit("NOTIFICATION", {
          type: "reaction",
          icon: {
            type: reaction.icon,
          },
          who: who,
        });
      });
    },
    sendEmote(emote) {
      this.displayReaction(emote);
      const data = JSON.stringify({
        icon: emote.icon,
        who: this.$store.state.participantId,
      });
      const session = this.$store.state.sessionObject;
      session.signal({
        data: data,
        type: "userReaction",
      });
    },
    timeToScheduleEntry(entry) {
      if (!entry) return "-";
      if (this.timeToSchedule(entry).asHours() >= 1) return this.timeToSchedule(entry).hours() + "h";
      if (this.timeToSchedule(entry).asMinutes() >= 1) return this.timeToSchedule(entry).minutes() + "m";
      if (this.timeToSchedule(entry).asSeconds() > 0) return "< 1m";
    },
    speedUpSchedule(entry) {
      const start = moment(this.$store.state.selectedActivity.date || this.$store.state.selectedActivityStartDate);
      this.$store.commit("SET_SERVER_TIME", start.add(entry.endDur).subtract(5, "seconds"));
    },
    scheduleLocationCheck() {
      setTimeout(this.checkLocationDataReceived, 120000);
    },
    checkLocationDataReceived() {
      if (this.$store.state.geolocation.positionWatch && this.$store.state.geolocation.trackHistory.length === 0) {
        this.triggerPositionUnavailableDialog = true;
      }
    },
    completeChallenge() {
      if (this.runEnded) return;

      this.$store.dispatch("DISABLE_GEOLOCATION");
      this.runEnded = true;
    },
    selfieUploaded() {
      if (this.$refs.participantWall) this.$refs.participantWall.refreshSelfies();
    },
    showParticipantWallIfJoiningLate() {
      let currentScheduleEntry = this.currentScheduleEntry;
      if (!currentScheduleEntry || currentScheduleEntry.order === 0 || currentScheduleEntry.selfieWallEmbedCode) return;

      let previousScheduleWithEmbedCode = null;
      for (let i = currentScheduleEntry.order - 1; i >= 0; i--) {
        let schedule = this.parsedSchedule.find((x) => x.order === i);
        if (schedule.selfieWallEmbedCode) {
          previousScheduleWithEmbedCode = schedule;
          break;
        }
      }

      if (previousScheduleWithEmbedCode) {
        this.selfieWallEmbedCode = previousScheduleWithEmbedCode.selfieWallEmbedCode;
      }
    },
    async loadParticipant() {
      const data = window.sessionStorage.getItem(this.$route.params["activity_slug"]);
      if (data) {
        const objectData = JSON.parse(data);
        this.participant = await axios.get(`/event/participants/${objectData.ot_token}`);
      }
    },
    getLobbyUrl() {
      const query = this.$route.query ? Object.assign({}, this.$route.query) : {};
      if (this.participant.firstName) {
        query.firstName = this.participant.firstName;
      }
      if (this.participant.lastName) {
        query.lastName = this.participant.lastName;
      }
      if (this.participant.username) {
        query.userName = this.participant.username;
      }
      if (window.sessionStorage.getItem("accessToken") !== "undefined") {
        query.accessToken = window.sessionStorage.getItem("accessToken");
      }
      if (this.$route.params["team_slug"] && this.$route.params["team_slug"] != "global") {
        query.team = this.$route.params.team_slug;
      }
      return `/e/${this.$store.state.event.slug}?${new URLSearchParams(query)}`;
    },
  },
  async mounted() {
    this.$store.commit("VIDEO_MUTED", this.iOS() || this.$store.state.isAutoJoining);
    let openTokData = this.$store.state.openTokData;
    window.sessionStorage.setItem("joinedActivity", this.$route.params["activity_slug"]);
    if (openTokData) {
      this.initEmotes();
    }
    await this.showParticipantWallIfJoiningLate();
    await this.loadParticipant();
  },
  async destroyed() {
    const session = this.$store.state.sessionObject;
    session.off("signal:userReaction");
    clearInterval(this.reactionNotificationInterval);
  },
};
</script>

<style lang="scss">
/* unscoped scss to allow appliance on dynamically appended iframe */

.video-wrapper {
  padding: 56.25% 0 0 0;
  width: 100%;
  max-height: 400px;
  min-height: 100%;
  position: relative;
  background: #222222;

  .video,
  .video-controls {
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
  }
  .video {
    pointer-events: none;
    max-height: 100vh;
  }

  .video {
    iframe {
      width: 100%;
      height: 100%;
    }
  }

  .video-controls {
    z-index: 1;
  }
}

.ended-schedule {
  opacity: 0.5;
}

.reaction-enter-active,
.reaction-leave-active {
  transition: all 0.6s;
  position: absolute;
  z-index: 2;
  bottom: 25px;
  width: 100%;
  left: 0;
}

.reaction-enter {
  left: 40%;
  width: 10%;
}

.gmap {
  width: 100%;
  height: 180px;
}

.overlay-container {
  position: relative;
}

.v-list-item__title span {
  vertical-align: middle;
}

.emoji-icon {
  width: 26px;
  height: 26px;
}

@media all and (-ms-high-contrast: none) {
  *::-ms-backdrop,
  .emoji-icon {
    margin-left: -5px;
  } /* IE11 */
}

.reaction-btn {
  width: 40px !important;
  min-width: 40px !important;
  height: 40px !important;
  border-radius: 50% !important;
}

.image-share-overlay {
  bottom: 0 !important;
  top: auto !important;
  height: 48px !important;
}

.v-application--is-ltr .v-window__next,
.v-application--is-ltr .v-window__prev {
  background: rgba(255, 255, 255, 0.6);
  border-radius: 0;
  top: 0;
  padding: 0;
  bottom: 0;
  margin: 0;
}

.v-application--is-ltr .v-window__next .v-btn--icon.v-size--default,
.v-application--is-ltr .v-window__prev .v-btn--icon.v-size--default {
  height: 100%;
}

.v-application--is-ltr .v-window__next .v-btn.v-btn--icon,
.v-application--is-ltr .v-window__prev .v-btn.v-btn--icon {
  color: black;
}
</style>
